<script setup lang="ts">
/**
 * Investor bank-account directory detail body — renders identically
 * inside the drawer chrome OR on a page wrapped in `<Box padding="4">`.
 * Props in, nothing else.
 *
 * The directory has no detail endpoint today — the body renders entirely
 * from the selected list row. The "decryptedAccountNumber" field is read
 * defensively so the day the BE starts returning it, the full number
 * surfaces automatically; otherwise the masked form is shown.
 *
 * Layout follows the canonical drawer-chrome pattern
 * (`ui-kit-theme.md → Drawer chrome for form drawers`): the body fills
 * the panel via `min-height: 100%`, sections sit at the top, and the
 * primary "View transactions" CTA pins to the viewport bottom via
 * `margin-top: auto`. The sibling kebab row action does the same — both
 * go through the parent container's event.
 */
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import CopyButton from '@uiKit/components/CopyButton.vue'
import { formatDate, maskAccountNumber } from '@shared/utils'
import type { BankAccountRow } from '../types/bankAccountsDirectoryTypes'

const props = defineProps<{
  row: BankAccountRow
}>()

const emit = defineEmits<{
  (e: 'view-transactions', row: BankAccountRow): void
}>()

const investorFullName = computed(() => {
  const first = props.row.investor.firstName ?? ''
  const last = props.row.investor.lastName ?? ''
  return `${first} ${last}`.trim() || '—'
})

// The list row's account number is the masked (or last-4) wire field. If
// the BE ever surfaces a `decryptedAccountNumber` here, show it as-is.
const decryptedAccountNumber = computed<string | null>(() => {
  const candidate = (props.row as unknown as { decryptedAccountNumber?: unknown })
    .decryptedAccountNumber
  return typeof candidate === 'string' && candidate.length > 0 ? candidate : null
})

const accountNumberLabel = computed(() => {
  if (decryptedAccountNumber.value) return decryptedAccountNumber.value
  return maskAccountNumber(props.row.accountNumber) || '—'
})

const shortInvestorId = computed(() => {
  const id = props.row.investor.id
  if (id.length <= 14) return id
  return `${id.slice(0, 8)}…${id.slice(-4)}`
})

function handleViewTransactions() {
  emit('view-transactions', props.row)
}
</script>

<template>
  <Box flex direction="col" class="detail-body">
    <Box flex direction="col" gap="4" class="detail-body__content">
      <Box class="section">
        <Text
          type="caption-sm"
          transform="uppercase"
          weight="semibold"
          color="muted"
          class="section-label"
          >Bank details</Text
        >
        <Box flex direction="col" gap="2" class="rows">
          <Box v-if="row.bankName" flex justify="between">
            <Text type="body-sm" color="muted">Bank name</Text>
            <Text type="body-sm" weight="medium">{{ row.bankName }}</Text>
          </Box>
          <Box v-if="row.accountHolderName" flex justify="between">
            <Text type="body-sm" color="muted">Account holder</Text>
            <Text type="body-sm" weight="medium">{{ row.accountHolderName }}</Text>
          </Box>
          <Box flex justify="between">
            <Text type="body-sm" color="muted">Account number</Text>
            <Text type="body-sm" weight="medium" style="font-variant-numeric: tabular-nums">
              {{ accountNumberLabel }}
            </Text>
          </Box>
          <Box v-if="row.iban" flex justify="between">
            <Text type="body-sm" color="muted">IBAN</Text>
            <Text type="body-sm" weight="medium" style="font-variant-numeric: tabular-nums">
              {{ row.iban }}
            </Text>
          </Box>
          <Box v-if="row.swiftCode" flex justify="between">
            <Text type="body-sm" color="muted">SWIFT / BIC</Text>
            <Text type="body-sm" weight="medium">{{ row.swiftCode }}</Text>
          </Box>
          <Box v-if="row.ifscCode" flex justify="between">
            <Text type="body-sm" color="muted">IFSC</Text>
            <Text type="body-sm" weight="medium">{{ row.ifscCode }}</Text>
          </Box>
          <Box v-if="row.currencyCode" flex justify="between">
            <Text type="body-sm" color="muted">Currency</Text>
            <Text type="body-sm" weight="medium">{{ row.currencyCode }}</Text>
          </Box>
        </Box>
      </Box>

      <Box class="section">
        <Text
          type="caption-sm"
          transform="uppercase"
          weight="semibold"
          color="muted"
          class="section-label"
          >Investor</Text
        >
        <Box flex direction="col" gap="2" class="rows">
          <Box flex justify="between">
            <Text type="body-sm" color="muted">Name</Text>
            <Text type="body-sm" weight="medium">{{ investorFullName }}</Text>
          </Box>
          <Box v-if="row.investor.investorType" flex justify="between">
            <Text type="body-sm" color="muted">Investor type</Text>
            <Text type="body-sm" weight="medium">{{ row.investor.investorType }}</Text>
          </Box>
          <Box v-if="row.investor.id" flex justify="between" align="center" gap="2">
            <Text type="body-sm" color="muted">Investor ID</Text>
            <Box flex align="center" gap="2">
              <Text
                type="body-sm"
                weight="medium"
                style="font-variant-numeric: tabular-nums"
                :title="row.investor.id"
              >
                {{ shortInvestorId }}
              </Text>
              <CopyButton :value="row.investor.id" label="Copy" copied-label="Copied" />
            </Box>
          </Box>
        </Box>
      </Box>

      <Box class="section">
        <Text
          type="caption-sm"
          transform="uppercase"
          weight="semibold"
          color="muted"
          class="section-label"
          >Verification</Text
        >
        <Box flex direction="col" gap="2" class="rows">
          <Box flex justify="between">
            <Text type="body-sm" color="muted">Added at</Text>
            <Text type="body-sm" weight="medium">{{ formatDate(row.createdAt) || '—' }}</Text>
          </Box>
          <Box v-if="row.rejectionReason" flex justify="between" gap="3">
            <Text type="body-sm" color="muted">Rejection reason</Text>
            <Text type="body-sm" weight="medium" align="right">
              {{ row.rejectionReason }}
            </Text>
          </Box>
        </Box>
      </Box>
    </Box>

    <Box class="detail-body__actions">
      <Button
        label="View transactions"
        icon="arrow-right"
        icon-pos="right"
        color="primary"
        block
        size="large"
        @click="handleViewTransactions"
      />
    </Box>
  </Box>
</template>

<style scoped>
.detail-body {
  min-height: 100%;
  padding: 1.25rem 1.5rem;
}

.detail-body__content {
  flex: 1;
}

.detail-body__actions {
  margin-top: auto;
  padding-top: 1rem;
}

.section {
  border-top: 1px solid var(--p-content-border-color);
  padding-top: 1rem;
}
.section-label {
  display: block;
  margin-bottom: 0.5rem;
}
.rows {
  padding-top: 0.25rem;
}

@media (max-width: 992px) {
  .detail-body {
    padding: 1rem;
  }
}
</style>
